/**
 * Endpoints do terminal, tipados. Os tipos são escritos à mão e NÃO inferidos
 * do zod: inferir arrastaria `@vertex/validators` inteiro (e o zod junto) pro
 * bundle. O que é compartilhado de verdade — aritmética, constantes e as
 * formas do carrinho e do checkout — vem de `@vertex/validators/pdv-pure`,
 * que é livre de zod por construção.
 */
import type {
  PdvCheckoutBody,
  PdvCheckoutResult,
  PdvRegisterState,
  PdvShortcutConfig,
  PdvVariant,
} from "@vertex/validators/pdv-pure";
import { api } from "./api";

export interface PdvBranding {
  name: string | null;
  logoUrl: string | null;
  accentColor: string | null;
  bgColor: string | null;
  terminalTheme?: "dark" | "light";
}

export interface PdvContext {
  pdv: { code: string; unitName: string | null };
  branding: PdvBranding;
  operator: { id: string; name: string } | null;
  modules: { services: boolean; products: boolean };
  pendingPickups: number;
  paymentMethods: string[];
  /** `null` = mostra todos os atalhos. */
  tiles: string[] | null;
  /** `null` = teclas de fábrica; o registry resolve com `resolveBindings`. */
  shortcuts: PdvShortcutConfig | null;
  nfeMode: "always" | "never" | "ask";
  autoPrint: boolean;
  installments: { max: number; min: number | null };
  cardMachines: Array<{
    id: string;
    name: string;
    isDefault: boolean;
    fees: unknown[];
  }>;
  register: PdvRegisterState;
}

export interface PdvSale {
  id: string;
  number: string;
  docNumber: string | null;
  total: number;
  paymentMethod: string | null;
  createdAt: string;
  returnedAt: string | null;
}

/**
 * Linha crua do `/products/search`, como o controller devolve HOJE
 * (`pdv-public.controller.ts`): preços chegam como string (coluna
 * `numeric(12,2)`, o driver preserva a precisão) e o saldo vem em `available`
 * (`null` = item sem controle de estoque; o server não manda mais
 * `trackStock`/`stock`). A conversão para número acontece num lugar só, aqui.
 */
interface RawVariant {
  id: string;
  sku: string;
  barcode: string | null;
  name: string | null;
  productName: string;
  price: string;
  promoPrice: string | null;
  minPrice: string | null;
  unit: string | null;
  isCombo: boolean;
  available: number | string | null;
  allowBackorder?: boolean;
  image: string | null;
}

function toVariant(r: RawVariant): PdvVariant {
  return {
    id: r.id,
    sku: r.sku,
    barcode: r.barcode,
    name: r.name,
    productName: r.productName,
    price: Number(r.price),
    promoPrice: r.promoPrice === null ? null : Number(r.promoPrice),
    minPrice: r.minPrice === null ? null : Number(r.minPrice),
    unit: r.unit,
    isCombo: Boolean(r.isCombo),
    // `available` null = serviço/sob encomenda (o server só calcula saldo de
    // quem rastreia): vira trackStock false + stock null, como o app espera.
    trackStock: r.available !== null && r.available !== undefined,
    allowBackorder: Boolean(r.allowBackorder),
    stock: r.available === null || r.available === undefined ? null : Number(r.available),
    image: r.image ?? null,
  };
}

export const pdvApi = {
  login: (loginUsername: string, password: string) =>
    api.post<{ pdv: { code: string }; branding: PdvBranding }>("/session", {
      loginUsername,
      password,
    }),

  logout: () => api.post<{ ok: true }>("/logout"),

  context: (signal?: AbortSignal) => api.get<PdvContext>("/context", undefined, signal),

  /** `q` vazio devolve os mais vendidos — é a vitrine de abertura do balcão. */
  async searchVariants(q: string, signal?: AbortSignal): Promise<PdvVariant[]> {
    // O endpoint responde `{ items, mode }` (o api-client já desembrulhou o
    // `data`): mapear o array direto quebrava o terminal novo com TypeError.
    const res = await api.get<{ items: RawVariant[]; mode: "search" | "bestsellers" }>(
      "/products/search",
      { q },
      signal,
    );
    return (res?.items ?? []).map(toVariant);
  },

  checkout: (body: PdvCheckoutBody) => api.post<PdvCheckoutResult>("/checkout", body),

  sales: (signal?: AbortSignal) => api.get<PdvSale[]>("/sales", undefined, signal),

  returnSale: (orderId: string) => api.post<{ ok: true }>("/return", { orderId }),

  register: {
    current: (signal?: AbortSignal) =>
      api.get<PdvRegisterState>("/register/current", undefined, signal),
    open: (openingFloat: number, operatorUserId?: string) =>
      api.post<PdvRegisterState>("/register/open", { openingFloat, operatorUserId }),
    movement: (type: "sangria" | "suprimento", amount: number, note: string) =>
      api.post<PdvRegisterState>("/register/movement", { type, amount, note }),
    close: (countedCash: number, notes?: string) =>
      api.post<{
        expectedCash: number;
        countedCash: number;
        difference: number;
      }>("/register/close", { countedCash, notes }),
  },

  operators: (signal?: AbortSignal) =>
    api.get<Array<{ id: string; name: string }>>("/operators", undefined, signal),

  customers: {
    search: (q: string, signal?: AbortSignal) =>
      api.get<Array<{ id: string; name: string; document: string | null; phone: string | null }>>(
        "/customers/search",
        { q },
        signal,
      ),
  },
};
